/* generated using openapi-typescript-codegen -- do not edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CompanyOut } from '../models/CompanyOut';
import type { CoverageOut } from '../models/CoverageOut';
import type { DigestOut } from '../models/DigestOut';
import type { NewsDeskOut } from '../models/NewsDeskOut';
import type { NewsItemOut } from '../models/NewsItemOut';
import type { ScorecardOut } from '../models/ScorecardOut';
import type { ScreenerRunOut } from '../models/ScreenerRunOut';
import type { TrumpClassRateOut } from '../models/TrumpClassRateOut';
import type { TrumpCompanyOut } from '../models/TrumpCompanyOut';
import type { TrumpStatementOut } from '../models/TrumpStatementOut';
import type { TrumpWatchOut } from '../models/TrumpWatchOut';
import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';
export class StocksService {
    constructor(public readonly httpRequest: BaseHttpRequest) {}
    /**
     * One company: identity, price, fundamentals, dossier
     * @returns CompanyOut Successful Response
     * @throws ApiError
     */
    public getCompany({
        isin,
        asOf,
    }: {
        isin: string,
        /**
         * Point-in-time date. Fundamentals are filtered to facts filed on or before it, so a past view cannot read a restatement that had not happened. Defaults to today.
         */
        asOf?: (string | null),
    }): CancelablePromise<CompanyOut> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/v1/stocks/companies/{isin}',
            path: {
                'isin': isin,
            },
            query: {
                'as_of': asOf,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Tier counts, source health, resolution outcomes and exclusions
     * 🔴 The honesty endpoint.
     *
     * A screener whose universe you cannot audit is a screener whose universe you do not know — and
     * the failure is silent: a broken liquidity filter excluding 8,000 names produces *"nothing
     * qualified today"*, which looks exactly like a quiet market.
     * @returns CoverageOut Successful Response
     * @throws ApiError
     */
    public getCoverage(): CancelablePromise<CoverageOut> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/v1/stocks/coverage',
        });
    }
    /**
     * Every digest sent, with its body
     * @returns DigestOut Successful Response
     * @throws ApiError
     */
    public getDigests({
        limit = 30,
    }: {
        limit?: number,
    }): CancelablePromise<Array<DigestOut>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/v1/stocks/digests',
            query: {
                'limit': limit,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Breaking news on held and BUY names, with the move since each landed
     * The News page's feed.
     *
     * ⛔ **Nothing here is a signal or a recommendation.** No rule reads these rows, no verdict moves,
     * and `stock_picks` does not know they exist. A headline is not a direction — the same feed
     * carries a trial success and an FDA rejection in the same shape.
     *
     * ⚠ **"Live" means the poller's cadence, which is two minutes.** The number is read from a column
     * the poller refreshed, not fetched per request, so it is as fresh as the last tick and the
     * response says when that was via `lastPriceAt`. A reader who cannot see the timestamp cannot
     * tell a moving price from a stalled one.
     * @returns NewsItemOut Successful Response
     * @throws ApiError
     */
    public getNews({
        hours = 48,
        sort = 'recent',
    }: {
        /**
         * How far back to look.
         */
        hours?: number,
        /**
         * recent is newest first; impact is the largest measured move; expected is the most material event type.
         */
        sort?: 'recent' | 'impact' | 'expected',
    }): CancelablePromise<Array<NewsItemOut>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/v1/stocks/news',
            query: {
                'hours': hours,
                'sort': sort,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * How fresh the News page's numbers are, and the gate its alerts were filtered by
     * Aggregates only — no headlines, no prices, nothing per-story.
     *
     * ⚠ **Same window as `GET /stocks/news`, and the caller must pass the same `hours`.** A strip
     * describing 48 hours above a list showing 24 is a header that disagrees with its own table.
     *
     * ⛔ Nothing here is a signal. These are counts describing the quality of what is displayed.
     * @returns NewsDeskOut Successful Response
     * @throws ApiError
     */
    public getNewsDesk({
        hours = 48,
    }: {
        /**
         * How far back to look.
         */
        hours?: number,
    }): CancelablePromise<NewsDeskOut> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/v1/stocks/news/desk',
            query: {
                'hours': hours,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * The honesty ledger
     * @returns ScorecardOut Successful Response
     * @throws ApiError
     */
    public getScorecard(): CancelablePromise<ScorecardOut> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/v1/stocks/scorecard',
        });
    }
    /**
     * The newest frozen screen run
     * 200 with `null` before the first run. See the module note on why that is not a 404.
     *
     * 🔴 **Named by screen, not by ruleset version — this was `Literal["r1", "g1"]` until
     * 2026-08-18.** `RULESET_VERSION` moved to `r2` that day, the job stopped writing `r1` runs, and
     * this endpoint went on asking for the newest `r1` — which froze the screener on that morning's
     * run with no way to advance. `?ruleset=r2` was a **422**, so the new cohort was not merely
     * hidden, it was unaskable.
     *
     * ⚠ A version is what published a run; a screen is what the reader came for. Only the second is
     * stable, so only the second belongs in a URL. `store.SCREEN_RULESETS` holds the mapping, and a
     * test asserts no version in the database is missing from it.
     *
     * ⚠ **The response still carries `rulesetVersion`** — the caller should be able to see which
     * ruleset produced what it is looking at. It just no longer has to know one to ask.
     * @returns any Successful Response
     * @throws ApiError
     */
    public getScreen({
        screen = 'value',
    }: {
        /**
         * Which screen: value is the eight-rule screen, growth the four-family.
         */
        screen?: 'value' | 'growth',
    }): CancelablePromise<(ScreenerRunOut | null)> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/v1/stocks/screen',
            query: {
                'screen': screen,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * A past run, exactly as it was published
     * 🔴 Reads the frozen `output` and never recomputes.
     *
     * *A screen you cannot reproduce is a screen you can rationalise* — and in twelve weeks, when the
     * scorecard disagrees with a memory, this is the endpoint that answers *what did it actually say?*
     * @returns ScreenerRunOut Successful Response
     * @throws ApiError
     */
    public getScreenRun({
        runId,
    }: {
        runId: string,
    }): CancelablePromise<ScreenerRunOut> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/v1/stocks/screen/{run_id}',
            path: {
                'run_id': runId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Presidential statements and the acts they became, with the move since each landed
     * The Trump Watch page's feed.
     *
     * ⛔ **Nothing here is a signal, a recommendation, or a claim of causation.** No rule reads these
     * rows, no verdict moves, and `stock_picks` does not know they exist. `pctSince` says the price
     * moved after he spoke — not that he moved it.
     *
     * ⛔ **He does not publish stock picks, and this endpoint does not pretend otherwise.** Measured
     * over three exhausted days: of 22 statements, 8 tagged `SPY`, 3 `USO`, 2 `EWC`, and one named
     * companies. The unit is *a statement and the names it touched*.
     *
     * ⚠ **The layer is the trust dimension and every row carries it.** A `federal_register` row is
     * authoritative and a median of five days old; a `wire` row is minutes old and is a headline. A
     * reader who cannot see which is which cannot weigh them, so `layer` and `lagDays` are never
     * omitted.
     * @returns TrumpStatementOut Successful Response
     * @throws ApiError
     */
    public getTrump({
        hours = 168,
        policyClass,
        layer,
        limit = 100,
    }: {
        /**
         * How far back to look.
         */
        hours?: number,
        /**
         * Filter to one class, e.g. `trade`.
         */
        policyClass?: (string | null),
        /**
         * Filter to one source layer.
         */
        layer?: ('truth' | 'wire' | 'whitehouse' | 'federal_register' | null),
        limit?: number,
    }): CancelablePromise<Array<TrumpStatementOut>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/v1/stocks/trump',
            query: {
                'hours': hours,
                'policyClass': policyClass,
                'layer': layer,
                'limit': limit,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Every statement that named one company, and what the price did after each
     * One company's mention history on Trump Watch.
     *
     * ⚠ **Matched against every symbol the publisher tagged**, not only the one the price was tracked
     * on — a company is named whenever the wire listed it, and keying on the priced symbol would hide
     * most of the mentions.
     *
     * ⛔ Nothing here is a signal. No rule reads it and `stock_picks` does not know it exists.
     * @returns TrumpCompanyOut Successful Response
     * @throws ApiError
     */
    public getTrumpCompany({
        ticker,
        days = 365,
        limit = 100,
    }: {
        ticker: string,
        /**
         * How far back to look.
         */
        days?: number,
        limit?: number,
    }): CancelablePromise<TrumpCompanyOut> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/v1/stocks/trump/company/{ticker}',
            path: {
                'ticker': ticker,
            },
            query: {
                'days': days,
                'limit': limit,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * How often each policy class appears, and the median move when it did
     * The Trump Watch page's summary strip.
     *
     * ⛔ **A count and a realised spread, never a ranking of what to buy.** `medianAbsPct` is how far
     * the tagged names moved after statements of this class, in the past. Read as an expected return
     * it becomes the forecast this feature exists to avoid, so the page labels the column as a count
     * and puts `priced` beside `total` to show how much of the corpus it rests on.
     * @returns TrumpClassRateOut Successful Response
     * @throws ApiError
     */
    public getTrumpRates({
        days = 90,
    }: {
        /**
         * Window in days.
         */
        days?: number,
    }): CancelablePromise<Array<TrumpClassRateOut>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/v1/stocks/trump/rates',
            query: {
                'days': days,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Companies Trump has named recently, grouped by what the value screen says about them
     * The Trump Watch page, company-first.
     *
     * ⛔ **Exposure is a reason to look; the screen is the reason to buy.** The verdict on every row
     * comes from the value screen and never from the statement. No rule reads this endpoint, no
     * verdict moves because of it, and `stock_picks` does not know it exists.
     *
     * ⛔ **`pctSince` is not attribution.** The price moved after he spoke. This page cannot and does
     * not claim he moved it.
     *
     * ⚠ **Four groups, not three.** `screened` means the screener evaluated this company and
     * published `NONE` — the most common verdict it publishes, 2,481 of the newest run's 2,884 rows.
     * `unscreened` means no pick row exists, because `stocks_daily` scores only names with a Trading
     * 212 price line: 2,870 of 8,560 universe members have one. Merging them would report a rejection
     * the screener never made.
     *
     * ⚠ **Both numbers and both dates travel with every row.** `capStale` says the share count behind
     * `marketCap` is over 400 days old; `readStale` says this row's price is materially older than the
     * freshest on the page. Neither is withheld, because a stale figure that looks current is worse
     * than one that is labelled.
     * @returns TrumpWatchOut Successful Response
     * @throws ApiError
     */
    public getTrumpWatch({
        days = 7,
        limit = 200,
    }: {
        /**
         * How far back to look.
         */
        days?: number,
        limit?: number,
    }): CancelablePromise<TrumpWatchOut> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/v1/stocks/trump/watch',
            query: {
                'days': days,
                'limit': limit,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
}
